'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { customFieldApi, type CustomField, type CustomFieldType, type CustomFieldStatus } from '@parts/api';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';

export default function CustomFieldsPage() {
  const { t } = useTranslation();
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<CustomFieldStatus | 'ALL'>('ACTIVE');
  const [filterGroup, setFilterGroup] = useState<string>('');
  const [groups, setGroups] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    loadFields();
    loadGroups();
  }, [filterStatus, filterGroup]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus !== 'ALL') {
        params.status = filterStatus;
      }
      if (filterGroup) {
        params.group = filterGroup;
      }
      const res = await customFieldApi.getAll(params);
      setFields(res.items);
    } catch (error) {
      console.error('Failed to load fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const res = await customFieldApi.getGroups();
      setGroups(res.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await customFieldApi.activate(id);
      toast.success(t.parts.fieldUpdatedSuccess);
      loadFields();
    } catch (error) {
      console.error('Failed to activate field:', error);
      toast.error('Failed to activate field');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await customFieldApi.deactivate(id);
      toast.success(t.parts.fieldUpdatedSuccess);
      loadFields();
    } catch (error) {
      console.error('Failed to deactivate field:', error);
      toast.error('Failed to deactivate field');
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t.parts.fieldDeleteConfirm,
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await customFieldApi.delete(id);
      toast.success(t.parts.fieldDeletedSuccess);
      loadFields();
    } catch (error) {
      console.error('Failed to delete field:', error);
      toast.error('Failed to delete field');
    }
  };

  const getFieldTypeLabel = (type: CustomFieldType) => {
    const typeMap: Record<CustomFieldType, string> = {
      TEXT: t.parts.fieldTypeText,
      NUMBER: t.parts.fieldTypeNumber,
      DECIMAL: t.parts.fieldTypeDecimal,
      DATE: t.parts.fieldTypeDate,
      DATETIME: t.parts.fieldTypeDatetime,
      BOOLEAN: t.parts.fieldTypeBoolean,
      SELECT: t.parts.fieldTypeSelect,
      MULTI_SELECT: t.parts.fieldTypeMultiselect,
      TEXTAREA: t.parts.fieldTypeTextarea,
      URL: t.parts.fieldTypeUrl,
      EMAIL: t.parts.fieldTypeEmail,
      PHONE: t.parts.fieldTypePhone,
      FILE: t.parts.fieldTypeFile,
      IMAGE: t.parts.fieldTypeImage,
      RICH_TEXT: t.parts.fieldTypeRichText,
      JSON: t.parts.fieldTypeJson,
    };
    return typeMap[type] || type;
  };

  const getStatusBadge = (status: CustomFieldStatus) => {
    const statusConfig = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-700', label: t.parts.activeField },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-700', label: t.parts.inactiveField },
      ARCHIVED: { bg: 'bg-red-100', text: 'text-red-700', label: t.parts.archivedField },
    };
    const config = statusConfig[status];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t.parts.customFieldManagement}
          </h1>
          <p className="text-gray-600">
            {t.parts.customFields}
          </p>
        </div>

        {/* Actions and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 justify-between">
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + {t.parts.createField}
            </button>
            <button
              onClick={() => window.location.href = '/parts/columns'}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {t.parts.columnManagement}
            </button>
          </div>

          <div className="flex gap-3">
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as CustomFieldStatus | 'ALL')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">{t.parts.status}: {t.common.all}</option>
              <option value="ACTIVE">{t.parts.activeField}</option>
              <option value="INACTIVE">{t.parts.inactiveField}</option>
              <option value="ARCHIVED">{t.parts.archivedField}</option>
            </select>

            {/* Group Filter */}
            {groups.length > 0 && (
              <select
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t.parts.fieldGroup}: {t.common.all}</option>
                {groups.map((group) => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Fields Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.parts.fieldName}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.parts.fieldKey}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.parts.fieldType}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.parts.fieldGroup}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.parts.status}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.common.attributes}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.common.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      {t.common.noData}
                    </td>
                  </tr>
                ) : (
                  fields.map((field) => (
                    <tr key={field.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {field.fieldName}
                        </div>
                        {field.fieldNameEn && (
                          <div className="text-sm text-gray-500">{field.fieldNameEn}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {field.fieldKey}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          {getFieldTypeLabel(field.fieldType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {field.group || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(field.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {field.required && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-orange-100 text-orange-700">
                              {t.parts.required}
                            </span>
                          )}
                          {field.searchable && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">
                              {t.parts.searchable}
                            </span>
                          )}
                          {field.sortable && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-indigo-100 text-indigo-700">
                              {t.parts.sortable}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {field.status === 'ACTIVE' && (
                            <button
                              onClick={() => handleDeactivate(field.id)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              {t.parts.deactivateField}
                            </button>
                          )}
                          {field.status === 'INACTIVE' && (
                            <button
                              onClick={() => handleActivate(field.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              {t.parts.activateField}
                            </button>
                          )}
                          <button
                            onClick={() => setEditingField(field)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t.common.edit}
                          </button>
                          <button
                            onClick={() => handleDelete(field.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            {t.common.delete}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-600">
          {t.common.total}: {fields.length} {t.parts.customFields}
        </div>
      </div>
    </div>
  );
}
